import { Actions, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator } from '@ngrx/store';
import { MonoTypeOperatorFunction, Observable, Subscription, filter, take } from 'rxjs';

export interface ActionWithPredicate<T extends ActionCreator<string, Creator>> {
  action: T;
  predicate: (action: ReturnType<T>) => boolean;
}

export type ACPredicate<T extends ActionCreator<string, Creator>> = ActionWithPredicate<T>;

export type ActionCreatorOrPredicate = ActionCreator | ActionWithPredicate<ActionCreator>;

export const cancelOnAction =
  <T>(
    actions$: Actions,
    actions: ActionCreatorOrPredicate | ActionCreatorOrPredicate[],
    cancelAction?: ActionCreator
  ): MonoTypeOperatorFunction<T> =>
  (source$) => {
    return new Observable<T>((subscriber) => {
      const actionsAsArray: ActionCreator[] = (Array.isArray(actions) ? actions : [actions]).map((action) => {
        if ('predicate' in action) {
          return action.action;
        }

        return action;
      });
      let sub: Subscription;
      let actionsSub: Subscription;

      const subToSource = () => {
        sub = source$.subscribe({
          next: (v) => subscriber.next(v),
          complete: () => subscriber.complete(),
          error: (err) => subscriber.error(err),
        });
      };

      const subToActions = () => {
        actionsSub = actions$
          .pipe(
            ofType(...actionsAsArray),
            filter((action) => {
              const allActions = Array.isArray(actions) ? actions : [actions];

              const actionWithPredicate = allActions.find((a) => 'predicate' in a && a.action.type === (action as Action).type) as
                | ActionWithPredicate<ActionCreator>
                | undefined;

              if (actionWithPredicate) {
                return actionWithPredicate.predicate(action);
              }

              return true;
            }),
            take(1)
          )
          .subscribe(() => {
            if (cancelAction) {
              subscriber.next(cancelAction() as T);
            }
            sub?.unsubscribe();
            subToSource();
            subToActions();
          });
      };

      subToSource();
      subToActions();

      return () => {
        sub?.unsubscribe();
        actionsSub?.unsubscribe();
      };
    });
  };
